// No React state needed here; this hook exposes pure validation helpers

export function useValidation() {
  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Input length validation
  const validateLength = (value: string, fieldName: string, maxLength: number): string => {
    if (value.length > maxLength) {
      return `${fieldName} must be ${maxLength} characters or less`;
    }
    return '';
  };

  // Name validation (letters, spaces, hyphens, apostrophes only)
  const validateName = (name: string, fieldName: string = 'Name'): string => {
    if (!name) return `Enter ${fieldName.toLowerCase()}`;
    if (!/^[a-zA-Z\s'-]+$/.test(name)) return `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`;
    if (name.trim().length < 2) return `${fieldName} must be at least 2 characters`;
    return '';
  };

  // Password validation logic
  const getPasswordError = (password: string): string => {
    if (!password) return 'Enter your password';
    if (!/[A-Z]/.test(password)) return 'Please enter a capital letter';
    if (!/[a-z]/.test(password)) return 'Please enter a small letter';
    if (!/\d/.test(password)) return 'Please enter a number';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return 'Please enter a special character';
    if (password.length < 8) return 'Password must be at least 8 characters';
    return '';
  };

  // Simple password validation - only checks if password exists
  const getSimplePasswordError = (password: string): string => {
    if (!password) return 'Password cannot be empty.';
    return '';
  };

  // Email validation
  const validateEmail = (email: string): string => {
    if (!email) return 'Enter your email';
    if (!emailRegex.test(email)) return 'Please enter a valid email address.';
    return '';
  };

  // Generic validate function for forms
  const validate = (fields: Record<string, unknown>, options: {
    requireNames?: string[],
    confirmFields?: { field: string, confirmField: string, message: string }[],
    requireTerms?: boolean,
    useSimplePassword?: boolean,
  } = {}) => {
    const isString = (v: unknown): v is string => typeof v === 'string'
    const isTruthyString = (v: unknown): v is string => isString(v) && v.trim() !== ''

    const errors: Record<string, string> = {};
    if (options.requireNames) {
      options.requireNames.forEach((name) => {
        if (!isTruthyString(fields[name])) {
          errors[name] = `Enter ${name.replace(/([A-Z])/g, ' $1').toLowerCase()}`;
        }
      });
    }
    if (Object.prototype.hasOwnProperty.call(fields, 'email')) {
      const emailVal = fields.email;
      if (!isString(emailVal)) {
        errors.email = 'Enter your email';
      } else {
        const emailError = validateEmail(emailVal);
        if (emailError) errors.email = emailError;
      }
    }
    if (Object.prototype.hasOwnProperty.call(fields, 'password')) {
      const pwd = isString(fields.password) ? fields.password : '';
      const passwordError = options.useSimplePassword
        ? getSimplePasswordError(pwd)
        : getPasswordError(pwd);
      if (passwordError) errors.password = passwordError;
    }
    if (options.confirmFields) {
      options.confirmFields.forEach(({ field, confirmField, message }) => {
        const a = fields[field];
        const b = fields[confirmField];
        if (isString(a) && isString(b) && a !== b) {
          errors[confirmField] = message;
        }
      });
    }
    if (options.requireTerms) {
      const accepted = (fields as Record<string, unknown>).termsAccepted;
      const isAccepted = typeof accepted === 'boolean' ? accepted : false;
      if (!isAccepted) {
        errors.terms = 'You must agree to the terms of service';
      }
    }
    return errors;
  };

  return {
    getPasswordError,
    getSimplePasswordError,
    validateEmail,
    validateName,
    validateLength,
    validate,
  };
} 